import React, { useEffect, useState } from 'react';

/**
 * COMPONENTE: MENCIONES OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el banner de Menciones (Formato A y Formato B) con
 * las mismas animaciones y clases del banner de cumpleañeros y mensajes.
 * ------------------------------------------------------------------
 */
const MencionesOverlay = ({ config }) => {
    const {
        visible,
        scale,
        position,
        offsetX,
        offsetY,
        bgColor,
        textColor,
        titleColor,
        fontFamily,
        activeMencion,
        isVertical,
        logoScale,
        logoRightScale,
        elementSpacing
    } = config;

    const [flowState, setFlowState] = useState('idle'); // idle, entering, visible, fadingOut
    const [currentMencion, setCurrentMencion] = useState(null);
    const [pendingMencion, setPendingMencion] = useState(null);

    // Sincronización de visibilidad y mención activa
    useEffect(() => {
        if (visible && activeMencion) {
            if (flowState === 'idle') {
                setCurrentMencion(activeMencion);
                setFlowState('entering');
            } else if (flowState === 'visible' && currentMencion?.id !== activeMencion.id) {
                setPendingMencion(activeMencion);
                setFlowState('fadingOut');
            }
        } else {
            setFlowState(prev => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
            setPendingMencion(null);
        }
    }, [visible, activeMencion, currentMencion, flowState]);

    // Transición de estados de animación
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                if (pendingMencion) {
                    setCurrentMencion(pendingMencion);
                    setPendingMencion(null);
                    setFlowState('entering');
                } else {
                    setCurrentMencion(null);
                    setFlowState('idle');
                }
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState, pendingMencion]);

    if (flowState === 'idle' || !currentMencion) return null;

    // Estilos del contenedor principal alineado según coordenadas
    const isCustom = position === 'custom';
    const containerStyle = {
        position: 'absolute',
        left: isCustom ? `${offsetX}%` : '50%',
        top: isCustom ? `${offsetY}%` : (position === 'top' ? '15%' : (position === 'center' ? '50%' : '85%')),
        transform: `translate(-50%, -50%) scale(${scale || 1})`,
        transformOrigin: 'center center',
        zIndex: 9999,
        // COMENTADO: Se respeta la fuente global para evitar conflictos
        // fontFamily: fontFamily ? `'${fontFamily}', sans-serif` : "'Montserrat', sans-serif"
    };

    const isFormatoA = currentMencion.formato === 'Formato A';

    return (
        <div 
            className="messages" 
            style={{ 
                bottom: `${offsetY}%`,
                left: '50%',
                transform: `translate(-50%, 0) scale(${scale ?? 1.0})`,
                transformOrigin: 'bottom center',
                transition: 'bottom 0.3s ease, transform 0.2s ease',
                zIndex: 9999,
                height: isVertical ? 'auto' : '180px'
            }}
        >
            {isVertical ? (
                /* --- DISEÑO VERTICAL --- */
                <div 
                    className={`messages__banner messages__banner--${flowState}`}
                    style={{ 
                        backgroundColor: bgColor || '#ff003c', 
                        backgroundImage: 'none',
                        //border: `4px solid ${textColor || '#ffffff'}`,
                        //borderRadius: '24px',
                        padding: '24px 36px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '320px',
                        // width: '90%',
                        // maxWidth: '700px',
                        boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
                        textAlign: 'center',
                        height: 'auto'
                    }}
                >
                    <video 
                        src="/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4" 
                        autoPlay 
                        loop 
                        muted 
                        playsInline 
                        className="messages__video" 
                        style={{ borderRadius: '20px' }}
                    />

                    {isFormatoA ? (
                        // --- Formato A en Vertical ---
                        <div className="messages__content" style={{ width: '100%', gap: `${(elementSpacing !== undefined ? elementSpacing : 0) + 24}px`, padding: 0 }}>
                            {currentMencion.logoDataUrl && (
                                <img 
                                    className="messages__logo" 
                                    src={currentMencion.logoDataUrl} 
                                    alt="Logo Mención" 
                                    style={{ height: `${(logoScale || 1.0) * 70}px`, width: `${(logoScale || 1.0) * 150}px`, objectFit: 'contain', animation: 'logoEnter 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
                                />
                            )}
                            {currentMencion.link && (
                                <div 
                                    className="messages__link-fluid"
                                    style={{ 
                                        border: `2px solid ${textColor || '#ffffff'}`, 
                                        color: textColor || '#ffffff',
                                        textShadow: `0 0 10px ${bgColor || '#ff003c'}`
                                    }}
                                >
                                    {currentMencion.link}
                                </div>
                            )}
                        </div>
                    ) : (
                        // --- Formato B en Vertical ---
                        <div className="messages__content" style={{ width: '100%', gap: '12px', padding: 0 }}>
                            {(currentMencion.logoDataUrl || currentMencion.logoRightDataUrl) && (
                                <div className="d-flex align-items-center justify-content-center my-1" style={{ gap: `${(elementSpacing !== undefined ? elementSpacing : 0) + 24}px` }}>
                                    {currentMencion.logoDataUrl && (
                                        <img 
                                            className="messages__logo" 
                                            src={currentMencion.logoDataUrl} 
                                            alt="Logo 1" 
                                            style={{ height: `${(logoScale || 1.0) * 38}px`, width: `${(logoScale || 1.0) * 80}px`, objectFit: 'contain', animation: 'logoEnter 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
                                        />
                                    )}
                                    {currentMencion.logoRightDataUrl && (
                                        <img 
                                            className="messages__logo" 
                                            src={currentMencion.logoRightDataUrl} 
                                            alt="Logo 2" 
                                            style={{ height: `${(logoScale || 1.0) * 38}px`, width: `${(logoScale || 1.0) * 80}px`, objectFit: 'contain', animation: 'logoEnter 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
                                        />
                                    )}
                                </div>
                            )}

                            {currentMencion.texto && (
                                <span 
                                    className="messages__text-fluid"
                                    style={{ 
                                        color: titleColor || '#ffffff', 
                                        textShadow: `0 0 10px ${bgColor || '#ff003c'}`,
                                        display: 'block', 
                                        margin: '0 0 4px 0'
                                    }}
                                >
                                    {currentMencion.texto}
                                </span>
                            )}

                            {currentMencion.link && (
                                <span 
                                    className="messages__link-fluid"
                                    style={{ 
                                        color: textColor || '#ffffff', 
                                        opacity: 0.9,
                                        border: 'none',
                                        padding: '0'
                                    }}
                                >
                                    {currentMencion.link}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                /* --- DISEÑO HORIZONTAL ORIGINAL --- */
                <div 
                    className={`messages__banner messages__banner--${flowState}`}
                    style={{ 
                        backgroundColor: bgColor || '#ff003c', 
                        backgroundImage: 'none',
                        //borderTop: `4px solid ${textColor || '#ffffff'}`,
                        //borderBottom: `4px solid ${textColor || '#ffffff'}`,
                        padding: '16px 120px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: `${(elementSpacing !== undefined ? elementSpacing : 0) + 24}px`,
                        boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
                        textAlign: 'center',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <video 
                        src="/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4" 
                        autoPlay 
                        loop 
                        muted 
                        playsInline 
                        className="messages__video" 
                    />

                    {isFormatoA ? (
                        // --- Formato A en Horizontal ---
                        <>
                            {currentMencion.logoDataUrl && (
                                <img 
                                    className="messages__logo messages__logo--left" 
                                    src={currentMencion.logoDataUrl} 
                                    alt="Logo Mención" 
                                    style={{ height: `${(logoScale || 1.0) * 70}px`, width: 'auto', objectFit: 'contain' }}
                                />
                            )}
                            {currentMencion.link && (
                                <div style={{ flexGrow: 0, flexShrink: 0, width: 'auto' }}>
                                    <div 
                                        className="messages__link-fluid"
                                        style={{ 
                                            border: `2px solid ${textColor || '#ffffff'}`, 
                                            color: textColor || '#ffffff', 
                                            textShadow: `0 0 10px ${bgColor || '#ff003c'}`,
                                            fontSize: '2.2rem',
                                            fontWeight: '900',
                                            padding: '10px 30px',
                                            borderRadius: '14px',
                                            display: 'inline-block'
                                        }}
                                    >
                                        {currentMencion.link}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        // --- Formato B en Horizontal ---
                        <div className="messages__content">
                            {(currentMencion.logoDataUrl || currentMencion.logoRightDataUrl) && (
                                <div className="d-flex align-items-center justify-content-center" style={{ gap: `${(elementSpacing !== undefined ? elementSpacing : 0) + 24}px`, margin: '2px 0' }}>
                                    {currentMencion.logoDataUrl && (
                                        <img 
                                            className="messages__logo messages__logo--left" 
                                            src={currentMencion.logoDataUrl} 
                                            alt="Logo Left" 
                                            style={{ height: `${(logoScale || 1.0) * 38}px`, width: 'auto', objectFit: 'contain' }}
                                        />
                                    )}
                                    {currentMencion.logoRightDataUrl && (
                                        <img 
                                            className="messages__logo messages__logo--right" 
                                            src={currentMencion.logoRightDataUrl} 
                                            alt="Logo Right" 
                                            style={{ height: `${(logoScale || 1.0) * 38}px`, width: 'auto', objectFit: 'contain' }}
                                        />
                                    )}
                                </div>
                            )}

                            {currentMencion.texto && (
                                <span 
                                    className="messages__text-fluid"
                                    style={{ 
                                        color: titleColor || '#ffffff', 
                                        textShadow: `0 0 10px ${bgColor || '#ff003c'}`,
                                        display: 'block', 
                                        margin: '0', 
                                        lineHeight: '1.0' 
                                    }}
                                >
                                    {currentMencion.texto}
                                </span>
                            )}

                            {currentMencion.link && (
                                <span 
                                    className="messages__link-fluid"
                                    style={{ 
                                        color: textColor || '#ffffff', 
                                        opacity: 0.9, 
                                        lineHeight: '1.0',
                                        border: 'none',
                                        padding: '0'
                                    }}
                                >
                                    {currentMencion.link}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { MencionesOverlay };
export default MencionesOverlay;
